import styled from '@emotion/styled';
import { Constants } from '@seedlang/constants';
import { SpacedRepetition } from '@seedlang/models';
import Button from 'components/button/button';
import Checkbox from 'components/checkbox';
import Text from 'components/text';
import { observer } from 'mobx-react';
import React from 'react';

const Module = styled.div`
  background: white;
  display: block;
  width: 100%;
  padding: 10px;
  margin-bottom: 10px;
  border-radius: 5px;
`;

const InnerModule = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: ${props => props.margin};
`;

const Buttons = styled.div`
  margin-top: 10px;
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
`;

const SimulateRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin-top: 14px;
`;

const DayControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DayInput = styled.input`
  width: 56px;
  height: 32px;
  padding: 0 6px;
  border: 1px solid #cacaca;
  border-radius: 4px;
  font-size: 14px;
  text-align: center;
`;

@observer
class IntervalTester extends React.Component {
  dayInMs = 24 * 60 * 60 * 1000;

  constructor(props) {
    super(props);
    this.state = this.stateFromCard(props.card);
  }

  componentDidUpdate(prevProps) {
    if (this.props.card !== prevProps.card) {
      this.setState(this.stateFromCard(this.props.card));
    }
    if (this.props.reviewsDueAtBeginningOfDay !== prevProps.reviewsDueAtBeginningOfDay) {
      const daysElapsed = this.roundedDaysElapsed(this.state.daysElapsed);
      if (daysElapsed !== this.state.daysElapsed) {
        this.setState({ daysElapsed });
      }
    }
  }

  roundedDaysElapsed(value) {
    if (!Number.isFinite(value)) {
      return 0;
    }
    if (this.props.reviewsDueAtBeginningOfDay) {
      return Math.round(value);
    }
    return Math.round(value * 10) / 10;
  }

  stateFromCard(card, daysElapsed = this.naturalElapsedDays(card)) {
    const offset = this.reviewTimeOffsetMsFromDaysElapsed(card, daysElapsed);
    return {
      daysElapsed,
      goodInterval: card?.formattedReviewInterval(Constants.GOOD_SCORE, {
        forceReviewAfterDue: this.props.forceReviewAfterDue,
        reviewTimeOffsetMs: offset,
      }) || '',
      easyInterval: card?.formattedReviewInterval(Constants.EASY_SCORE, {
        forceReviewAfterDue: this.props.forceReviewAfterDue,
        reviewTimeOffsetMs: offset,
      }) || '',
    };
  }

  naturalElapsedDays(card) {
    if (!card?.reviewAfter?.dateObj) {
      return 0;
    }
    const raw = SpacedRepetition.elapsedDaysSinceLastReview(
      card.reviewAfter.dateObj,
      card.repetitionInterval || 0,
      0,
    );
    return this.roundedDaysElapsed(raw);
  }

  reviewTimeOffsetMsFromDaysElapsed(card, daysElapsed) {
    const d = this.roundedDaysElapsed(daysElapsed);
    const ra = card?.reviewAfter?.dateObj;
    const scheduled = card?.repetitionInterval;
    if (ra && scheduled > 0) {
      const lastReviewedAt = new Date(ra.getTime() - scheduled * this.dayInMs);
      return lastReviewedAt.getTime() + d * this.dayInMs - Date.now();
    }
    return d * this.dayInMs;
  }

  setDaysElapsed(daysElapsed) {
    const value = Math.round(Number.isFinite(daysElapsed) ? daysElapsed : 0);
    this.setState(this.stateFromCard(this.props.card, value));
  }

  onDaysElapsedChange = (e) => {
    const v = e.target.value;
    if (v === '' || v === '-') {
      this.setDaysElapsed(0);
      return;
    }
    const n = parseInt(v, 10);
    this.setDaysElapsed(Number.isNaN(n) ? 0 : n);
  }

  adjustDaysElapsed = (delta) => {
    const cur = Math.round(Number.isFinite(this.state.daysElapsed) ? this.state.daysElapsed : 0);
    this.setDaysElapsed(cur + delta);
  }

  toggleReviewsDueAtBeginningOfDay = () => {
    this.props.onToggleIntervalTesterReviewsDueAtBeginningOfDay();
  }

  processScoreWithSync = (score) => {
    const offset = this.reviewTimeOffsetMsFromDaysElapsed(this.props.card, this.state.daysElapsed);
    this.props.processScore(score, {
      forceReviewAfterDue: this.props.forceReviewAfterDue,
      reviewTimeOffsetMs: offset,
      reviewsDueAtBeginningOfDay: this.props.reviewsDueAtBeginningOfDay,
    });
    if (score === Constants.GOOD_SCORE || score === Constants.EASY_SCORE) {
      const iv = this.props.card?.repetitionInterval;
      this.setDaysElapsed(Number.isFinite(iv) ? Math.round(iv) : 0);
    } else if (score === Constants.BAD_SCORE) {
      this.setDaysElapsed(this.naturalElapsedDays(this.props.card));
    }
  }

  render() {
    const { daysElapsed } = this.state;
    const offset = this.reviewTimeOffsetMsFromDaysElapsed(this.props.card, daysElapsed);

    return (
      <Module>
        <InnerModule>
          <Text
            textAlign='center'
            bold
            fontSize='18px'
            padding='10px 0 20px 0'
          >
            Interval Tester (Admin Only)
          </Text>
          <ToggleRow>
            <Checkbox
              value={this.props.reviewsDueAtBeginningOfDay}
              onClick={this.toggleReviewsDueAtBeginningOfDay}
            />
            <Text
              display='inline'
              fontSize='14px'
            >
              Reviews Due at Beginning of Day
            </Text>
          </ToggleRow>
          <Buttons>
            <Button
              margin='0 5px'
              onClick={() => this.processScoreWithSync(Constants.BAD_SCORE)}
            >
              1 Minute
            </Button>
            <Button
              margin='0 5px'
              onClick={() => this.processScoreWithSync(Constants.GOOD_SCORE)}
            >
              {this.props.card.formattedReviewInterval(Constants.GOOD_SCORE, {
                forceReviewAfterDue: this.props.forceReviewAfterDue,
                reviewTimeOffsetMs: offset,
                reviewsDueAtBeginningOfDay: this.props.reviewsDueAtBeginningOfDay,
              })}
            </Button>
            <Button
              margin='0 5px'
              onClick={() => this.processScoreWithSync(Constants.EASY_SCORE)}
            >
              {this.props.card.formattedReviewInterval(Constants.EASY_SCORE, {
                forceReviewAfterDue: this.props.forceReviewAfterDue,
                reviewTimeOffsetMs: offset,
                reviewsDueAtBeginningOfDay: this.props.reviewsDueAtBeginningOfDay,
              })}
            </Button>
          </Buttons>
          <SimulateRow>
            <Text
              fontSize='14px'
            >
              Days elapsed
            </Text>
            <DayControls>
              <Button
                margin='0'
                fontSize='13px'
                padding='6px 10px'
                minWidth='32px'
                height='32px'
                onClick={() => this.adjustDaysElapsed(-1)}
              >
                −
              </Button>
              <DayInput
                type='number'
                step={this.props.reviewsDueAtBeginningOfDay ? 1 : 0.1}
                value={Number.isFinite(this.state.daysElapsed) ? this.state.daysElapsed : ''}
                onChange={this.onDaysElapsedChange}
              />
              <Button
                margin='0'
                fontSize='13px'
                padding='6px 10px'
                minWidth='32px'
                height='32px'
                onClick={() => this.adjustDaysElapsed(1)}
              >
                +
              </Button>
            </DayControls>
          </SimulateRow>
        </InnerModule>
      </Module>
    );
  }
}

export default IntervalTester;
